import { Card, Empty, Typography } from "antd";

const { Text } = Typography;

const defaultText = (value) => (value === undefined || value === null || value === "" ? "-" : String(value));
const defaultMoney = (value) => defaultText(value);
const defaultHours = (value) => defaultText(value);
const defaultList = () => [];
const defaultNumber = () => 0;

const PaystubDetailsCard = ({
  payment = {},
  row = null,
  period = {},
  employer = {},
  ytdTotals = {},
  staffName = "",
  staffEmail = "",
  staffPhone = "",
  employeeAddress = "",
  employerName = "",
  employerAddress = "",
  employerContact = "",
  money = defaultMoney,
  formatHours = defaultHours,
  payRateDisplayParts = defaultList,
  computedPayrollTaxLinesFor = defaultList,
  computedPayrollTaxAmountFor = defaultNumber,
  hasAnyPayRate = () => true,
  regularPayAmountFor = defaultNumber,
  overtimePayAmountFor = defaultNumber,
  payrollItemAmount = defaultNumber,
  emptyDescription = "No approved timesheets found for this staff member and pay period.",
}) => {
  if (!row) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} />;
  }

  const currency = payment?.currency || row?.currency || "USD";
  const rateParts = payRateDisplayParts(row);
  const payrollTaxAmount = computedPayrollTaxAmountFor(row);
  const payrollTaxLines = computedPayrollTaxLinesFor(row);
  const totalTaxesAndDeductions = (
    Number(row.preTaxDeductionsAmount || 0) +
    Number(row.postTaxDeductionsAmount || 0) +
    Number(payrollTaxAmount || 0) +
    Number(row.federalTax?.amount || 0)
  );
  const netPay = hasAnyPayRate(row) ? money(row.estimatedPay, currency) : "-";

  return (
    <div className="payroll-paystub-print-area">
      <Card className="payroll-paystub-card" size="small">
        <div className="payroll-paystub-party-grid">
          <section>
            <Text type="secondary">Employer</Text>
            <h3>{employerName || employer?.name || payment?.workspaceName || "Organization"}</h3>
            <p>{employerAddress || employer?.address || payment?.workspaceAddress || "No organization address on record"}</p>
            <p>{employerContact || [employer?.email, employer?.phone].map((value) => String(value || "").trim()).filter(Boolean).join(" | ") || "No contact on record"}</p>
          </section>
          <section>
            <Text type="secondary">Employee</Text>
            <h3>{staffName || "Employee"}</h3>
            <p>{employeeAddress || "No address on record"}</p>
            <p>{[staffEmail, staffPhone].map((value) => String(value || "").trim()).filter(Boolean).join(" | ") || "No contact on record"}</p>
          </section>
        </div>

        <div className="payroll-paystub-meta">
          <span><small>Pay period</small><strong>{defaultText(period.start)} - {defaultText(period.end)}</strong></span>
          <span><small>Pay date</small><strong>{defaultText(period.payDate)}</strong></span>
          <span><small>Approved hours</small><strong>{formatHours(row.approvedHours)}</strong></span>
          <span><small>Regular</small><strong>{formatHours(row.regularHours)}</strong></span>
          <span><small>Overtime</small><strong>{formatHours(row.overtimeHours)}</strong></span>
          <span>
            <small>Pay rate</small>
            <strong className="payroll-payrate-value">
              <span className="payroll-payrate-lines">
                {(rateParts.length ? rateParts : ["Not set"]).map((label) => (
                  <em key={label}>{label}</em>
                ))}
              </span>
            </strong>
          </span>
          <span><small>Payroll taxes</small><strong>{money(payrollTaxAmount, currency)}</strong></span>
          <span className="payroll-paystub-net">
            <small>Net pay</small>
            <strong>{netPay}</strong>
          </span>
        </div>

        <div className="payroll-paystub-columns">
          <div className="payroll-paystub-table">
            <div className="payroll-paystub-table-title">Earnings</div>
            <div className="payroll-paystub-line">
              <span>Regular pay</span>
              <em>
                {String(row.payRateType || "").toUpperCase() === "SALARY"
                  ? "Salary"
                  : `${formatHours(row.regularHours)} x ${money(row.hourlyRate, currency)}`}
              </em>
              <strong>{money(regularPayAmountFor(row), currency)}</strong>
            </div>
            {Number(row.overtimeHours || 0) > 0 ? (
              <div className="payroll-paystub-line">
                <span>Overtime pay</span>
                <em>{formatHours(row.overtimeHours)} x {money(row.overtimeRate, currency)}</em>
                <strong>{money(overtimePayAmountFor(row), currency)}</strong>
              </div>
            ) : null}
            {row.activeEarnings?.map((item) => (
              <div className="payroll-paystub-line" key={item.id || item.name}>
                <span>{item.name}</span>
                <em>{item.scope === "USER" ? "Staff only" : "All staff"} | {item.taxable === false ? "Not taxable" : "Taxable"}</em>
                <strong>{money(payrollItemAmount(item, row.basePay), currency)}</strong>
              </div>
            ))}
            <div className="payroll-paystub-line is-total">
              <span>Gross pay</span>
              <em />
              <strong>{money(row.grossPay, currency)}</strong>
            </div>
          </div>

          <div className="payroll-paystub-table">
            <div className="payroll-paystub-table-title">Taxes and deductions</div>
            {row.activeDeductions?.map((item) => (
              <div className="payroll-paystub-line" key={item.id || item.name}>
                <span>{item.name}</span>
                <em />
                <strong>{money(payrollItemAmount(item, row.grossPay), currency)}</strong>
              </div>
            ))}
            {payrollTaxLines.map((item) => (
              <div className="payroll-paystub-line" key={item.id || item.code || item.name}>
                <span>{item.name}</span>
                <em />
                <strong>{money(payrollItemAmount(item, row.grossPay), currency)}</strong>
              </div>
            ))}
            <div className="payroll-paystub-line">
              <span>Federal income tax</span>
              <em />
              <strong>{money(row.federalTax?.amount || 0, currency)}</strong>
            </div>
            <div className="payroll-paystub-line is-taxable">
              <span>Taxable pay</span>
              <em />
              <strong>{money(row.taxablePay, currency)}</strong>
            </div>
            <div className="payroll-paystub-line is-total">
              <span>Total deductions and taxes</span>
              <em />
              <strong>{money(totalTaxesAndDeductions, currency)}</strong>
            </div>
          </div>
        </div>

        <div className="payroll-paystub-bottom-summary">
          <span>
            <small>Gross pay</small>
            <strong>{money(row.grossPay, currency)}</strong>
          </span>
          <span>
            <small>Net pay</small>
            <strong>{netPay}</strong>
          </span>
        </div>

        {Object.keys(ytdTotals || {}).length ? (
          <div className="payroll-paystub-meta payroll-paystub-ytd-summary">
            <span><small>YTD gross pay</small><strong>{money(ytdTotals.grossPay, currency)}</strong></span>
            <span><small>YTD net pay</small><strong>{money(ytdTotals.netPay, currency)}</strong></span>
            <span><small>YTD deductions</small><strong>{money(ytdTotals.totalDeductions, currency)}</strong></span>
            <span><small>YTD hours</small><strong>{formatHours(ytdTotals.approvedHours)}</strong></span>
          </div>
        ) : null}
      </Card>
    </div>
  );
};

export default PaystubDetailsCard;
